import { useMemo, useState } from 'react';
import { Alert, Badge, Chip, type DataTableColumn } from '@zarya/ui';
import { t } from '@app/i18n';
import { useMultiSite } from '@app/main/Sites/Sites.api';
import { agentState } from '@app/main/Resources/resourceVocab';
import { CapabilityGrid } from './CapabilityGrid';
import {
  delegationTip,
  notAcceptsLimit,
  warningSpecialistA2A,
  type AccessControlState,
  type A2ARow,
} from './AccessControlModel';
import type { AccessDraftApi } from './useAccessControlDraft';
import s from './AccessControl.module.css';

export interface OutgoingTabProps {
  estado: AccessControlState;
  rascunho: AccessDraftApi;
  editavel: boolean;
}

/**
 * Aba **Saída A2A** (FR-072, FR-074): as delegações que o contrato desta
 * identidade concede.
 *
 * DESDE O DEF-29 (09/09) ESTA ABA É UMA LISTA DE CAPABILITIES
 *
 *   Uma delegação deixou de ser uma lista à parte e passou a ser capability do
 *   contrato, espécie `agent`: mesma lista, mesmos dois eixos de uma tool (a
 *   pessoa pode E a identidade pode), mesmo caminho de avaliação. O rascunho a
 *   manda em `capabilities[]` com `kind: agent`; o campo `a2a[]` do candidato
 *   ficou deprecado e a tela não o envia mais.
 *
 *   A grade é a **mesma** da aba Tools (`CapabilityGrid`): busca, «Somente
 *   concedidas», contador, marcar na linha. Duas grades escritas em separado
 *   divergiriam — e divergiram: a pílula existia numa e não na outra.
 *
 *   O que não se copia é a coluna de limite. Uma delegação não aceita limite
 *   (`delegationNoLimit`, 422 no contrato): `agents[]` do documento do PDP é uma
 *   lista de slugs, sem lugar onde um teto pudesse ser aplicado. A coluna diz
 *   isso em vez de calar, como a aba Tools diz na tool sem `constrainable`.
 *
 * Especialista com delegação de saída sai do desenho de delegação em estrela (só
 * o generalista chama outros agentes) — a tela explica antes de o diff marcar.
 */
export function OutgoingTab({ estado, rascunho, editavel }: OutgoingTabProps) {
  const [search, setBusca] = useState('');
  const [grantedOnly, setGrantedOnly] = useState(false);

  const filtradas = useMemo(() => {
    const term = search.trim().toLowerCase();
    return estado.a2a_out.filter((alvo) => {
      const concedida = rascunho.draft.a2a[alvo.resource_id] ?? false;
      const text = `${alvo.name} ${alvo.path} ${alvo.slug ?? ''}`.toLowerCase();
      return (!term || text.includes(term)) && (!grantedOnly || concedida);
    });
  }, [search, estado.a2a_out, rascunho.draft.a2a, grantedOnly]);

  const someGranted = estado.a2a_out.some((alvo) => rascunho.draft.a2a[alvo.resource_id]);
  const isEspecialista = estado.identity.role === 'specialist';
  const multiSite = useMultiSite();

  const columns: DataTableColumn<A2ARow>[] = [
    {
      key: 'concedida',
      header: '',
      width: '44px',
      cell: (alvo) => (
        <input
          type="checkbox"
          className={s.checkbox}
          checked={rascunho.draft.a2a[alvo.resource_id] ?? false}
          disabled={!editavel}
          aria-label={t('identityAccess.outgoing.grant.aria', { slug: alvo.name })}
          onChange={() => rascunho.alternarA2A(alvo.resource_id)}
        />
      ),
    },
    {
      key: 'agente',
      header: t('identityAccess.outgoing.col.agent'),
      tip: delegationTip(),
      cell: (alvo) => (
        <span className={s.tool}>
          <b>{alvo.name}</b>
        </span>
      ),
    },
    {
      // Fase 2: a lista é de RECURSOS AGENTE (protótipo aprovado, prancha `Identity-access`). A ligação diz se o
      // agente é uma identidade do console ou um agente externo.
      key: 'ligacao',
      header: t('identityAccess.outgoing.col.binding'),
      cell: (alvo) =>
        alvo.scope === 'external' ? (
          <Badge tone="neutral">{t('identityAccess.outgoing.binding.external')}</Badge>
        ) : alvo.slug ? (
          t('identityAccess.outgoing.binding.identity', { slug: alvo.slug })
        ) : (
          <span className={s.muted}>{t('identityAccess.outgoing.binding.lost')}</span>
        ),
    },
    ...(multiSite
      ? [
          {
            key: 'site',
            header: t('identityAccess.outgoing.col.site'),
            cell: (alvo: A2ARow) => (alvo.site_slug ? <Chip>{alvo.site_slug}</Chip> : '—'),
          } satisfies DataTableColumn<A2ARow>,
        ]
      : []),
    {
      key: 'contrato',
      header: t('identityAccess.outgoing.col.contract'),
      cell: (alvo) =>
        alvo.contract_version ? `v${alvo.contract_version}` : <span className={s.muted}>—</span>,
    },
    {
      key: 'skills',
      header: t('identityAccess.outgoing.col.skills'),
      cell: (alvo) => String(alvo.skills),
    },
    {
      key: 'estado',
      header: t('identityAccess.outgoing.col.state'),
      cell: (alvo) => {
        const state = agentState(alvo.state);
        return (
          <Badge tone={state.tone}>
            {alvo.state === 'untrusted_target'
              ? `${state.rotulo} · ${t('identityAccess.outgoing.state.denied')}`
              : state.rotulo}
          </Badge>
        );
      },
    },
    {
      key: 'limite',
      header: t('identityAccess.outgoing.col.limit'),
      cell: () => <span className={s.muted}>{notAcceptsLimit()}</span>,
    },
  ];

  return (
    <CapabilityGrid
      label={t('identityAccess.outgoing.label')}
      columns={columns}
      rows={filtradas}
      rowKey={(alvo) => alvo.resource_id}
      search={search}
      onSearchChange={setBusca}
      searchPlaceholder={t('identityAccess.outgoing.searchPlaceholder')}
      grantedOnly={grantedOnly}
      onGrantedOnlyChange={setGrantedOnly}
      countLabel={t('identityAccess.outgoing.count', {
        n: filtradas.length,
        total: estado.a2a_out.length,
      })}
      onClearFilters={() => {
        setBusca('');
        setGrantedOnly(false);
      }}
      filtersApplied={Boolean(search || grantedOnly)}
      emptyIcon="robot"
      emptyTitle={t('identityAccess.outgoing.empty.title')}
      emptyMessage={t('identityAccess.outgoing.empty.message')}
    >
      <p className={s.nota}>{t('identityAccess.outgoing.note')}</p>
      {isEspecialista && someGranted ? (
        <Alert tone="warning" title={t('identityAccess.outgoing.specialist.title')}>
          {warningSpecialistA2A()}
        </Alert>
      ) : null}
    </CapabilityGrid>
  );
}
